import Keycloak from 'keycloak-js';
import { config } from '@/config/appConfig';
import environment from '@/config/environment';
import { auditLogger } from './auditLogger';
import { tokenEncryptionService } from './tokenEncryptionService';

export interface KeycloakUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  roles: string[];
}

export interface KeycloakTokenInfo {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
}

class KeycloakService {
  private keycloak: Keycloak.KeycloakInstance | null = null;
  private isInitialized = false;
  private initPromise: Promise<boolean> | null = null;
  private isMockMode = false;
  private mockTokens: { accessToken: string; refreshToken: string; expiresIn: number } | null = null;
  private mockAuthenticated = false;
  private sessionCheckInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Don't initialize in constructor - wait for explicit init call
    this.isMockMode = environment.mockKeycloak === true;
  }

  /**
   * Generate a mock JWT token with proper claims
   */
  private generateMockToken(username: string = 'admin'): string {
    const header = { alg: 'RS256', typ: 'JWT' };
    const now = Math.floor(Date.now() / 1000);
    const payload = {
      sub: `mock-user-${username}`,
      iss: 'mock-keycloak',
      aud: 'moneyfellows-admin',
      exp: now + 3600, // 1 hour
      iat: now,
      azp: 'moneyfellows-admin',
      realm_access: {
        roles: ['admin', 'user'],
      },
      resource_access: {
        'moneyfellows-admin': {
          roles: ['admin'],
        },
      },
      preferred_username: username,
      email: `${username}@moneyfellows.com`,
      name: username.charAt(0).toUpperCase() + username.slice(1),
      given_name: username.charAt(0).toUpperCase() + username.slice(1),
      family_name: 'User',
    };

    // Base64 encode (note: this is a simplified mock, not cryptographically signed)
    const encodedHeader = btoa(JSON.stringify(header));
    const encodedPayload = btoa(JSON.stringify(payload));
    const signature = 'mock-signature';

    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  /**
   * Initialize mock Keycloak for development/testing
   */
  private async initializeMockKeycloak(): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 2: Initializing MOCK Keycloak');
    }

    // Generate mock tokens
    this.mockTokens = {
      accessToken: this.generateMockToken('admin'),
      refreshToken: this.generateMockToken('admin'),
      expiresIn: 3600,
    };
    
    this.mockAuthenticated = true;
    this.isInitialized = true;

    // Store encrypted tokens
    await this.storeEncryptedTokens();

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Step 2: Mock Keycloak initialized with tokens');
    }
  }

  /**
   * Store encrypted tokens in localStorage
   */
  private async storeEncryptedTokens(): Promise<void> {
    try {
      if (this.isMockMode && this.mockTokens) {
        await tokenEncryptionService.storeAccessToken(this.mockTokens.accessToken);
        await tokenEncryptionService.storeRefreshToken(this.mockTokens.refreshToken);
      } else if (this.keycloak?.token) {
        await tokenEncryptionService.storeAccessToken(this.keycloak.token);
        if (this.keycloak.refreshToken) {
          await tokenEncryptionService.storeRefreshToken(this.keycloak.refreshToken);
        }
      }
    } catch (error) {
      console.error('Failed to store encrypted tokens:', error);
      // Continue without encrypted storage - tokens remain in memory
    }
  }

  /**
   * Load encrypted tokens from localStorage
   */
  private async loadEncryptedTokens(): Promise<{ accessToken: string | null; refreshToken: string | null }> {
    try {
      const accessToken = await tokenEncryptionService.getAccessToken();
      const refreshToken = await tokenEncryptionService.getRefreshToken();
      return { accessToken, refreshToken };
    } catch (error) {
      console.error('Failed to load encrypted tokens:', error);
      return { accessToken: null, refreshToken: null };
    }
  }

  /**
   * Clear encrypted tokens from localStorage
   */
  private clearEncryptedTokens(): void {
    tokenEncryptionService.clearTokens();
  }

  /**
   * Clean up legacy unencrypted tokens and stale Keycloak state
   * SECURITY: Remove plaintext tokens in production, keep in development for debugging
   */
  private cleanupLegacyStorage(): void {
    // In production, remove any old unencrypted tokens
    if (process.env.NODE_ENV !== 'development') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 [PRODUCTION] Removed plaintext tokens from storage');
      }
    }
    
    // Clean up stale Keycloak callback state (all environments)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('kc-callback-')) {
        localStorage.removeItem(key);
      }
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Cleaned up Keycloak callback state');
    }
  }

  /**
   * Initialize Keycloak instance with all required configurations
   * This should be called when login button is pressed
   * IMPORTANT: This method is idempotent - it won't recreate the instance if it already exists
   */
  private async initializeKeycloak(): Promise<void> {
    // Already initialized - skip
    if (this.keycloak) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Keycloak instance already exists, skipping initialization');
      }
      return;
    }

    // Mock mode: skip real Keycloak initialization
    if (this.isMockMode) {
      await this.initializeMockKeycloak();
      return;
    }

    const keycloakConfig = config.auth.keycloak;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 2: Initializing Keycloak with configuration:', {
        url: keycloakConfig.issuer,
        realm: keycloakConfig.realm,
        clientId: keycloakConfig.clientId,
        redirectUri: keycloakConfig.redirectUri,
      });
    }
    
    try {
      this.keycloak = new (Keycloak as any)({
        url: keycloakConfig.issuer,
        realm: keycloakConfig.realm,
        clientId: keycloakConfig.clientId,
        redirectUri: keycloakConfig.redirectUri,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 2: Keycloak instance created successfully');
      }

      // Set up event listeners
      this.setupEventListeners();
    } catch (error) {
      console.error('❌ Step 2: Failed to create Keycloak instance:', error);
      this.keycloak = null;
      throw error;
    }
  }

  /**
   * Set up Keycloak event listeners with enhanced logging
   */
  private setupEventListeners(): void {
    if (!this.keycloak) return;

    this.keycloak.onReady = (authenticated) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 *onReady* - Keycloak ready, authenticated:', authenticated);
      }
      this.isInitialized = true;
    };

    this.keycloak.onAuthSuccess = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ *onAuthSuccess* - Keycloak authentication successful');
      }
      auditLogger.logUserAction('KEYCLOAK_AUTH_SUCCESS', {
        userId: this.getUserId(),
        timestamp: new Date().toISOString(),
      });
    };

    this.keycloak.onAuthError = (error) => {
      console.error('❌ *onAuthError* - Keycloak authentication error:', error);
      auditLogger.logUserAction('KEYCLOAK_AUTH_ERROR', {
        error: error.error || 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    };

    this.keycloak.onAuthRefreshSuccess = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 *onAuthRefreshSuccess* - Keycloak token refresh successful');
      }
    };

    this.keycloak.onAuthRefreshError = () => {
      console.error('❌ *onAuthRefreshError* - Keycloak token refresh failed');
      // Force re-authentication
      this.login();
    };

    this.keycloak.onTokenExpired = async () => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ *onTokenExpired* - Keycloak token expired, attempting refresh...');
      }
      const refreshed = await this.refreshToken();
      
      if (!refreshed) {
        console.error('❌ Token refresh failed - session may be expired');
        // Don't auto-logout here - let the next API call handle it
        // This prevents false positives from network issues
      }
    };

    this.keycloak.onAuthLogout = () => {
      console.warn('🚪 *onAuthLogout* - Keycloak session ended on server (idle timeout, logout, or admin termination)');
      auditLogger.logUserAction('KEYCLOAK_SESSION_EXPIRED', {
        reason: 'Server-side session expiration, logout, or admin termination',
        timestamp: new Date().toISOString(),
      }).catch(err => {
        // Don't block logout on audit log failure
        if (process.env.NODE_ENV === 'development') {
          console.warn('Audit log failed (non-blocking):', err);
        }
      });
      
      // Clear local state immediately
      this.clearAuth();
      
      // Use setTimeout to ensure state is cleared before redirect
      setTimeout(() => {
        // Redirect to login page
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Redirecting to login page after session termination...');
        }
        window.location.href = '/user/login';
      }, 100);
    };
  }

  /**
   * Initialize Keycloak with configuration
   * This should be called after Keycloak instance is created
   * @param onLoad - The onLoad option for Keycloak init ('check-sso' or 'login-required')
   */
  async init(onLoad: 'check-sso' | 'login-required' = 'check-sso'): Promise<boolean> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 init() called with onLoad:', onLoad, {
        isMockMode: this.isMockMode,
        hasKeycloak: !!this.keycloak,
        isInitialized: this.isInitialized,
        authenticated: this.keycloak?.authenticated,
      });
    }

    // Mock mode: already initialized
    if (this.isMockMode) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 3: Mock Keycloak already initialized (skip init)');
      }
      return this.mockAuthenticated;
    }

    // If Keycloak instance doesn't exist, create it first
    // This is needed when opening new tabs - the instance may not exist yet
    if (!this.keycloak) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Keycloak instance not found, creating it...');
      }
      await this.initializeKeycloak();
      
      if (!this.keycloak && !this.isMockMode) {
        throw new Error('Failed to create Keycloak instance');
      }
    }

    // If onLoad is 'login-required', force re-initialization
    if (onLoad === 'login-required') {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 *init* *login-required* - Forcing re-initialization with login-required');
      }
      this.isInitialized = false;
      this.initPromise = null;
    }

    // If already initialized and authenticated, return current state
    // This prevents unnecessary re-initialization when opening new tabs
    if (this.isInitialized && this.keycloak?.authenticated) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Keycloak already initialized and authenticated - returning true');
      }
      return true;
    }

    // Prevent multiple concurrent init calls
    if (this.initPromise) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏳ Init already in progress, waiting...');
      }
      return this.initPromise;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Starting performInit()...');
    }

    this.initPromise = this.performInit(onLoad);
    return this.initPromise;
  }

  private async performInit(onLoad: 'check-sso' | 'login-required' = 'check-sso'): Promise<boolean> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Step 3: Initializing Keycloak adapter...');
        console.log('🔧 Step 3: Init options:', {
          onLoad,
          checkLoginIframe: false,
        });
      }

      // Note: redirectUri is NOT passed to init() - it's only used in login() and logout()
      // The init() method automatically detects callback parameters in the current URL
      // and exchanges the authorization code for tokens without needing redirectUri
      // 
      // onLoad options:
      // - 'check-sso': Checks for existing SSO session, may redirect with error=login_required
      // - 'login-required': Automatically redirects to Keycloak login if no session exists
      
      // Disable iframe to avoid CSP errors with Keycloak server
      // CSP: "frame-ancestors 'self'" blocks iframe embedding
      // Fallback: periodic session checks (every 30s) still work
      const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      // Disable iframe in all environments to avoid Keycloak CSP violations
      // Keycloak server has CSP: script-src 'none' which blocks its own step2.html
      const enableIframe = false; // Disabled due to Keycloak CSP restrictions
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Session monitoring configuration:', {
          hostname: window.location.hostname,
          isLocalDevelopment,
          enableIframe,
          iframeInterval: enableIframe ? 60 : 'disabled'
        });
      }
      
      const initOptions: any = {
        onLoad: onLoad,
        checkLoginIframe: enableIframe, // ✅ ENABLE in staging/production, DISABLE in localhost (CORS)
      };
      
      // Only set interval if iframe is enabled
      if (enableIframe) {
        initOptions.checkLoginIframeInterval = 30; // Poll every 30s (more frequent detection)
        
        // silentCheckSsoRedirectUri is only used during initial 'check-sso' init, not for periodic polling
        // Periodic polling uses Keycloak's built-in login-status-iframe.html/init endpoint
        // Only set this if we're doing initial check-sso (not needed for periodic polling)
        if (onLoad === 'check-sso') {
          initOptions.silentCheckSsoRedirectUri = `${window.location.origin}/auth/silent-check.html`;
        }
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Step 3: Init options:', initOptions);
        if (enableIframe) {
          console.log('📡 Keycloak iframe polling enabled:');
          console.log('   - Initial SSO check: silent-check.html (called once during init)');
          console.log('   - Periodic polling: login-status-iframe.html/init (every 30s, hidden iframe)');
          console.log('   - Check Network tab → Filter: "login-status-iframe" to see periodic requests');
        }
      }
      
      const authenticated = await this.keycloak!.init(initOptions);

      this.isInitialized = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 3: Keycloak initialized successfully:', {
          authenticated,
          checkLoginIframe: enableIframe,
          environment: isLocalDevelopment ? 'localhost' : 'production',
          token: this.keycloak!.token ? 'present' : 'none',
          refreshToken: this.keycloak!.refreshToken ? 'present' : 'none',
        });
        
        if (enableIframe) {
          console.log('✅ Periodic iframe polling active - will check session every 30s');
          console.log('   Monitor: Network tab → Filter: "login-status-iframe"');
        } else {
          console.log('ℹ️  Keycloak iframe check disabled in localhost (prevents CORS errors)');
          console.log('ℹ️  Session monitoring still active via onTokenExpired event and periodic checks');
        }
      }

      // Store encrypted tokens after successful authentication
      if (authenticated && this.keycloak!.token) {
        await this.storeEncryptedTokens();
        
        // Clean up legacy unencrypted tokens and stale Keycloak state
        this.cleanupLegacyStorage();
      }

      // Start periodic session check as fallback (especially if iframe is disabled)
      // This checks session validity every 60 seconds by attempting token refresh
      if (authenticated) {
        this.startPeriodicSessionCheck();
      }

      return !!authenticated;
    } catch (error) {
      console.error('❌ Step 3: Failed to initialize Keycloak adapter:', error);
      this.isInitialized = false;
      this.initPromise = null;
      return false;
    }
  }

  /**
   * Get current authentication status
   */
  isAuthenticated(): boolean {
    if (this.isMockMode) {
      return this.mockAuthenticated;
    }
    return this.keycloak?.authenticated || false;
  }

  /**
   * Get current access token
   * Falls back to encrypted storage if not in memory
   */
  async getToken(): Promise<string | null> {
    if (this.isMockMode) {
      if (this.mockTokens?.accessToken) {
        return this.mockTokens.accessToken;
      }
      // Try to load from encrypted storage
      const tokens = await this.loadEncryptedTokens();
      return tokens.accessToken;
    }
    
    if (this.keycloak?.token) {
      return this.keycloak.token;
    }
    
    // Try to load from encrypted storage
    const tokens = await this.loadEncryptedTokens();
    return tokens.accessToken;
  }

  /**
   * Get current access token (synchronous version for backwards compatibility)
   */
  getTokenSync(): string | null {
    if (this.isMockMode) {
      return this.mockTokens?.accessToken || null;
    }
    return this.keycloak?.token || null;
  }

  /**
   * Get current refresh token
   * Falls back to encrypted storage if not in memory
   */
  async getRefreshToken(): Promise<string | null> {
    if (this.isMockMode) {
      if (this.mockTokens?.refreshToken) {
        return this.mockTokens.refreshToken;
      }
      // Try to load from encrypted storage
      const tokens = await this.loadEncryptedTokens();
      return tokens.refreshToken;
    }
    
    if (this.keycloak?.refreshToken) {
      return this.keycloak.refreshToken;
    }
    
    // Try to load from encrypted storage
    const tokens = await this.loadEncryptedTokens();
    return tokens.refreshToken;
  }

  /**
   * Get current refresh token (synchronous version for backwards compatibility)
   */
  getRefreshTokenSync(): string | null {
    if (this.isMockMode) {
      return this.mockTokens?.refreshToken || null;
    }
    return this.keycloak?.refreshToken || null;
  }

  /**
   * Get token information
   */
  getTokenInfo(): KeycloakTokenInfo | null {
    if (this.isMockMode && this.mockTokens) {
      return {
        accessToken: this.mockTokens.accessToken,
        refreshToken: this.mockTokens.refreshToken,
        expiresIn: this.mockTokens.expiresIn * 1000,
        tokenType: 'Bearer',
      };
    }

    if (!this.keycloak?.token) return null;

    return {
      accessToken: this.keycloak.token,
      refreshToken: this.keycloak.refreshToken || undefined,
      expiresIn: this.keycloak.tokenParsed?.exp ? 
        (this.keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000)) * 1000 : 0,
      tokenType: 'Bearer',
    };
  }

  /**
   * Get current user information
   */
  getUser(): KeycloakUser | null {
    if (this.isMockMode && this.mockAuthenticated && this.mockTokens) {
      // Parse mock token to extract user info
      try {
        const tokenParts = this.mockTokens.accessToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        
        return {
          id: payload.sub || '',
          name: payload.name || payload.preferred_username || '',
          email: payload.email || '',
          avatar: undefined,
          roles: [...(payload.realm_access?.roles || [])],
        };
      } catch (error) {
        console.error('Failed to parse mock token:', error);
        return null;
      }
    }

    if (!this.keycloak?.authenticated || !this.keycloak.tokenParsed) {
      return null;
    }

    const token = this.keycloak.tokenParsed;
    const realmAccess = token.realm_access;
    const resourceAccess = token.resource_access;

    // Extract roles from token
     const roles: string[] = [];
    // if (realmAccess?.roles) {
    //   roles.push(...realmAccess.roles);
    // }
    if (resourceAccess) {
      Object.values(resourceAccess).forEach((access: any) => {
        if (access.roles) {
          roles.push(...access.roles);
        }
      });
    }

    return {
      id: token.sub || '',
      name: (token as any).name || (token as any).preferred_username || '',
      email: (token as any).email || '',
      avatar: (token as any).picture || undefined,
      roles: roles.filter(role => !role.startsWith('offline_access') && !role.startsWith('uma_authorization')),
    };
  }

  /**
   * Get user ID
   */
  getUserId(): string | null {
    return this.keycloak?.tokenParsed?.sub || null;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    return this.keycloak?.isTokenExpired() || true;
  }

  /**
   * Refresh token if needed
   * IMPORTANT: Returns true for both "refreshed" AND "still valid" states
   * Only returns false if refresh was actually needed but failed (exception thrown)
   */
  async refreshToken(): Promise<boolean> {
    if (!this.keycloak) return false;

    try {
      // updateToken returns:
      // - true: Token was refreshed successfully
      // - false: Token is still valid and didn't need refresh
      // Both are success cases!
      const refreshed = await this.keycloak.updateToken(30); // Refresh if expires within 30 seconds
      
      // If token was actually refreshed, update encrypted storage
      if (refreshed && this.keycloak.token) {
        await this.storeEncryptedTokens();
      }
      
      // Return true for both cases (refreshed OR still valid)
      // This prevents false positives where "still valid" is interpreted as "failed"
      return true;
    } catch (error) {
      // Only return false if refresh was actually attempted but failed
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Initialize Keycloak and force login by using 'login-required' option
   * This is typically used when error=login_required is received
   * The 'login-required' option will automatically redirect to Keycloak SSO login page
   */
  async initWithLoginRequired(): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 1: Initializing Keycloak with login-required option...');
    }

    // Initialize Keycloak instance if needed
    if (!this.isInitialized || !this.keycloak) {
      await this.initializeKeycloak();
    }

    // Initialize with 'login-required' - will auto-redirect to Keycloak login
    await this.init('login-required');
    
    // Note: If successful, Keycloak will redirect to SSO login page
    // Code after this may not execute
  }

  /**
   * Login with Keycloak SSO
   * This is the main login method that should be called from the login button
   */
  async login(): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 1: Login button pressed - starting Keycloak login flow');
    }

    // Step 2: Initialize Keycloak instance if not already done OR if destroyed after logout
    if (!this.isInitialized || !this.keycloak) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Step 2: Initializing Keycloak instance...');
      }
      await this.initializeKeycloak();
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 2: Keycloak instance already exists, proceeding to init...');
      }
    }

    // Mock mode: skip Keycloak SSO, just mark as authenticated
    if (this.isMockMode) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 2-4: Mock Keycloak login complete (no redirect needed)');
      }
      // Mock is already authenticated after initializeKeycloak()
      return;
    }

    if (!this.keycloak) {
      console.error('❌ Keycloak instance is null after initialization');
      throw new Error('Failed to initialize Keycloak instance');
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 3: Calling Keycloak adapter init()...');
    }

    // Step 3: Initialize Keycloak adapter
    const authenticated = await this.init();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 3: Keycloak adapter initialized, authenticated:', authenticated);
    }

    // If already authenticated (init found callback params and exchanged tokens), don't redirect
    if (authenticated) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 3: Already authenticated (from callback), skipping SSO redirect');
      }
      return;
    }

    // Step 4: Perform SSO login (this will redirect to Keycloak)
    const keycloakConfig = config.auth.keycloak;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Step 4: Not authenticated, performing SSO login (will redirect)...');
      console.log('🔧 Step 4: RedirectUri being sent:', keycloakConfig.redirectUri);
      console.log('🔧 Step 4: Full login options:', {
        redirectUri: keycloakConfig.redirectUri,
        scope: keycloakConfig.scopes.join(' '),
      });
    }
    
    await this.keycloak.login({
      redirectUri: keycloakConfig.redirectUri,
      scope: keycloakConfig.scopes.join(' '),
    } as any);
  }

  /**
   * Logout from Keycloak
   * This method calls Keycloak logout endpoint to terminate server-side session
   * Note: Local storage is already cleared by authService, but Keycloak instance is preserved
   * After logout completes (or fails), clearAuth() will be called to clean up instance
   */
  async logout(): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🚪 KeycloakService: Calling Keycloak logout endpoint to terminate server session...');
    }

    // For mock mode, clear instance and redirect
    if (this.isMockMode) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚪 Mock mode logout - clearing instance and redirecting');
      }
      this.clearAuth();
      window.location.href = '/user/login';
      return;
    }

    // For real Keycloak: Call logout endpoint to end SSO session
    // Note: Local storage already cleared by authService, but instance must be preserved
    if (!this.keycloak) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ No Keycloak instance - clearing state and redirecting');
      }
      this.clearAuth();
      window.location.href = '/user/login';
      return;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚪 KeycloakService: Calling Keycloak logout endpoint (terminating SSO session)...');
        console.log('🚪 Logout redirect URI:', `${window.location.origin}/user/login`);
      }

      // Try the standard keycloak.logout() first
      // This will terminate the session on Keycloak server and redirect
      try {
        await this.keycloak.logout({
          redirectUri: `${window.location.origin}/user/login`,
        });
        
        // Note: Code after this won't execute because keycloak.logout() redirects immediately
        // The Keycloak server session is now terminated
      } catch (logoutError: any) {
        // If keycloak.logout() fails (e.g., 400 Bad Request), try manual logout
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ Keycloak logout() failed, trying manual logout URL:', logoutError.message);
        }
        
        // Manual logout using direct URL construction
        // This constructs the Keycloak logout endpoint URL manually
        const keycloakConfig = config.auth.keycloak;
        const logoutUrl = new URL(`${keycloakConfig.issuer}/protocol/openid-connect/logout`);
        logoutUrl.searchParams.set('client_id', keycloakConfig.clientId);
        logoutUrl.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/user/login`);
        
        // Optionally include id_token_hint if we have the token
        // Some Keycloak configurations require this
        if (this.keycloak.token) {
          logoutUrl.searchParams.set('id_token_hint', this.keycloak.token);
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🚪 Manual logout URL (will terminate session):', logoutUrl.toString());
        }
        
        // Clear Keycloak instance before redirect (cleanup)
        this.clearAuth();
        
        // Redirect to manual logout URL - this will terminate the session
        window.location.href = logoutUrl.toString();
      }
    } catch (error) {
      console.error('❌ Error during Keycloak logout:', error);
      // If logout completely fails, clear instance and redirect anyway
      this.clearAuth();
      window.location.href = '/user/login';
    }
  }

  /**
   * Get Keycloak account URL
   */
  getAccountUrl(): string {
    if (!this.keycloak) return '';
    return this.keycloak.createAccountUrl();
  }

  /**
   * Get Keycloak management URL
   */
  getManagementUrl(): string {
    if (!this.keycloak) return '';
    return this.keycloak.createLogoutUrl();
  }

  /**
   * Start periodic session validity check
   * This is a CRITICAL fallback when Keycloak's iframe polling stops (known issue)
   * Uses updateToken(-1) to check validity WITHOUT refreshing (doesn't reset idle timer)
   * Checks every 30 seconds to match iframe interval
   */
  private startPeriodicSessionCheck(): void {
    // Clear existing interval if any
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }

    // Skip if mock mode
    if (this.isMockMode) return;
    
    const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    // Disable iframe to avoid Keycloak CSP violations in production
    const enableIframe = false; // Disabled due to Keycloak CSP restrictions
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Starting active session monitoring (fallback for iframe polling)...');
      console.log('   - Keycloak iframe polling may stop after first call (known issue)');
      console.log('   - Active monitoring validates session every 30s using updateToken(-1)');
      console.log('   - updateToken(-1) checks validity WITHOUT refreshing (preserves idle timer)');
    }

    // Retry logic: allow 2 consecutive failures before logging out
    // This tolerates temporary network issues without false logouts
    let failureCount = 0;
    const maxFailures = 2;

    // Check session validity every 30 seconds (matches iframe interval)
    // IMPORTANT: Use updateToken(-1) which only checks validity, doesn't refresh
    this.sessionCheckInterval = setInterval(async () => {
      if (!this.keycloak || !this.keycloak.authenticated) {
        // Session already terminated, clear interval
        if (this.sessionCheckInterval) {
          clearInterval(this.sessionCheckInterval);
          this.sessionCheckInterval = null;
        }
        return;
      }

      try {
        // Check session validity by attempting token refresh ONLY if token is expired
        // This validates with server without unnecessary refreshes when token is still valid
        const isExpired = this.keycloak.isTokenExpired();
        
        if (isExpired) {
          // Token is expired - call updateToken to validate with server
          // This will either refresh successfully (session valid) or throw (session terminated)
          await this.keycloak.updateToken(30);
          
          // If we get here, token was refreshed successfully - session is valid
          if (failureCount > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Active session check: Token refreshed, session valid (recovered from ${failureCount} failures)`);
            }
            failureCount = 0;
          }
        } else {
          // Token is still valid locally - check authenticated flag (updated by iframe if working)
          // Since iframe polling stopped, we can't rely on it, but we also don't want to refresh unnecessarily
          // The authenticated flag should still be reliable from the initial iframe check
          if (!this.keycloak.authenticated) {
            // Authenticated flag says we're logged out - session terminated
            throw new Error('Session terminated - authenticated flag is false');
          }
          
          // Session appears valid (authenticated flag is true and token not expired)
          if (failureCount > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Active session check: Session appears valid (token not expired, authenticated=true)`);
            }
            failureCount = 0;
          }
        }
      } catch (error: any) {
        // updateToken throws if session is terminated on server
        // This is the reliable way to detect server-side session termination
        failureCount++;
        if (process.env.NODE_ENV === 'development') {
          console.warn(`⚠️ Active session check: Session validation failed (${failureCount}/${maxFailures}):`, error?.error || error?.message || error);
        }
        
        // Only logout after max consecutive failures (tolerates network hiccups)
        if (failureCount >= maxFailures) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ Active session check: Maximum validation failures reached - session terminated');
            console.error('   This indicates Keycloak server session was terminated (logout, idle timeout, or admin action)');
          }
          // Trigger onAuthLogout to handle cleanup and redirect
          if (this.keycloak.onAuthLogout) {
            this.keycloak.onAuthLogout();
          }
          // Clear interval
          if (this.sessionCheckInterval) {
            clearInterval(this.sessionCheckInterval);
            this.sessionCheckInterval = null;
          }
        }
      }
    }, 30000); // Check every 30 seconds (matches iframe interval)
  }

  /**
   * Stop periodic session check
   */
  private stopPeriodicSessionCheck(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
      if (process.env.NODE_ENV === 'development') {
        console.log('🛑 Stopped periodic session check');
      }
    }
  }

  /**
   * Clear authentication state
   */
  clearAuth(): void {
    // Stop periodic session check
    this.stopPeriodicSessionCheck();

    // Clear encrypted tokens from localStorage
    this.clearEncryptedTokens();
    
    // Clean up legacy storage and Keycloak state
    this.cleanupLegacyStorage();

    if (this.isMockMode) {
      this.mockTokens = null;
      this.mockAuthenticated = false;
      this.isInitialized = false;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Mock Keycloak authentication state cleared');
      }
      return;
    }

    // Clear tokens and destroy Keycloak instance
    if (this.keycloak) {
      this.keycloak.clearToken();
    }
    
    // Destroy the Keycloak instance to force fresh initialization on next login
    this.keycloak = null;
    this.isInitialized = false;
    this.initPromise = null;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Keycloak instance destroyed and state cleared');
    }
  }

  /**
   * Get Keycloak instance (for advanced usage)
   */
  getKeycloakInstance(): Keycloak.KeycloakInstance | null {
    return this.keycloak;
  }

}

export const keycloakService = new KeycloakService();
export default keycloakService;
